import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Countdown from 'react-countdown';
import './App.css';
import NewsFeed from './components/NewsFeed';
import ChatBox from './components/ChatBox';
import ViewerCounter from './components/ViewerCounter';
import { faGear, faChevronUp, faChevronDown, faNewspaper, faComments } from '@fortawesome/free-solid-svg-icons';
import ErrorPage from './components/ErrorPage';
import Widget from './components/Widget';


const CountdownApp = () => {
  const getSavedValue = (key, defaultValue) => {


    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  };

  const [showCustomizationPanel, setShowCustomizationPanel] = useState(false);
  const [countdownTheme] = useState(getSavedValue('countdownTheme', 'default'));
  const [background, setBackground] = useState(getSavedValue('background', 'Official_Cover_Art_landscape.webp'));
  const [currentBg, setCurrentBg] = useState(background);
  const [prevBg, setPrevBg] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(getSavedValue('isFooterVisible', true));

  const [showControls, setShowControls] = useState(true);
  const [isNewsFeedOpen, setIsNewsFeedOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const [showFooterToggle, setShowFooterToggle] = useState(getSavedValue('showFooterToggle', true));
  const [showNewsToggle, setShowNewsToggle] = useState(getSavedValue('showNewsToggle', true));
  const [showChatToggle, setShowChatToggle] = useState(getSavedValue('showChatToggle', true));
  const [autoChangeBg, setAutoChangeBg] = useState(getSavedValue('autoChangeBg', false));
  const [showWidgetSection, setShowWidgetSection] = useState(false);
  const [widgetBg, setWidgetBg] = useState('default');
  const [widgetSizePreset, setWidgetSizePreset] = useState('medium');
  const [widgetWidth, setWidgetWidth] = useState(380);
  const [widgetHeight, setWidgetHeight] = useState(240);
  const backgroundSelectRef = useRef(null);

  const videoRef = useRef(null);
  const mouseTimeoutRef = useRef(null);
  const isFirstRender = useRef(true);


  const releaseDate = new Date(2026, 10, 19, 0, 0, 0);
  const twoWeeksBefore = new Date(releaseDate.getTime() - 14 * 24 * 60 * 60 * 1000);
  const isNearRelease = new Date() >= twoWeeksBefore;

  useEffect(() => {
    localStorage.setItem('countdownTheme', JSON.stringify(countdownTheme));
  }, [countdownTheme]);

  useEffect(() => {
    localStorage.setItem('background', JSON.stringify(background));
  }, [background]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPrevBg(currentBg);
    setCurrentBg(background);
    setIsTransitioning(true);

    const timeout = setTimeout(() => {
      setIsTransitioning(false);
      setPrevBg(null);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [background]);


  useEffect(() => {
    localStorage.setItem('isFooterVisible', JSON.stringify(isFooterVisible));
  }, [isFooterVisible]);

  useEffect(() => {
    localStorage.setItem('showFooterToggle', JSON.stringify(showFooterToggle));
  }, [showFooterToggle]);

  useEffect(() => {
    localStorage.setItem('showNewsToggle', JSON.stringify(showNewsToggle));
  }, [showNewsToggle]);

  useEffect(() => {
    localStorage.setItem('showChatToggle', JSON.stringify(showChatToggle));
  }, [showChatToggle]);

  useEffect(() => {
    localStorage.setItem('autoChangeBg', JSON.stringify(autoChangeBg));
  }, [autoChangeBg]);

  useEffect(() => {
    if (!autoChangeBg) return;

    const interval = setInterval(() => {
      setBackground((currentBg) => {
        if (!backgroundSelectRef.current) return currentBg;
        const options = Array.from(backgroundSelectRef.current.options)
          .map(opt => opt.value)
          .filter(val => !val.endsWith('.mp4'));
        if (options.length === 0) return currentBg;
        const currentIndex = options.indexOf(currentBg);
        const nextIndex = (currentIndex + 1) % options.length;
        return options[nextIndex];
      });
    }, 20000);

    return () => clearInterval(interval);
  }, [autoChangeBg]);
  useEffect(() => {
    if (widgetSizePreset === 'small') {
      setWidgetWidth(320);
      setWidgetHeight(150);
    } else if (widgetSizePreset === 'medium') {
      setWidgetWidth(380);
      setWidgetHeight(240);
    } else if (widgetSizePreset === 'large') {
      setWidgetWidth(450);
      setWidgetHeight(320);
    }
  }, [widgetSizePreset]);



  useEffect(() => {
    const CURRENT_VERSION = '1.2.3';

    const checkVersion = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/version.json?t=${Date.now()}`, {
          cache: 'no-store'
        });
        const data = await response.json();

        if (data.version && data.version !== CURRENT_VERSION) {
          console.log(`New version ${data.version} detected (Current: ${CURRENT_VERSION}). Reloading...`);
          window.location.reload();
        }
      } catch (error) {
        console.warn('Unable to check for updates:', error);
      }
    };

    const interval = setInterval(checkVersion, 60000);

    checkVersion();

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);

      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }

      mouseTimeoutRef.current = setTimeout(() => {
        if (!showCustomizationPanel && !isNewsFeedOpen && !isChatOpen) {
          setShowControls(false);
        }
      }, 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    handleMouseMove();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
    };
  }, [showCustomizationPanel, isNewsFeedOpen, isChatOpen]);

  const toggleCustomizationPanel = () => {
    setShowCustomizationPanel(!showCustomizationPanel);
    if (!showCustomizationPanel) {
      setIsNewsFeedOpen(false);
      setIsChatOpen(false);
    }
  };

  const toggleNewsFeed = () => {
    setIsNewsFeedOpen(!isNewsFeedOpen);
    if (!isNewsFeedOpen) {
      setShowCustomizationPanel(false);
      setIsChatOpen(false);
    }
  };

  const closeNewsFeed = () => {
    setIsNewsFeedOpen(false);
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setShowCustomizationPanel(false);
      setIsNewsFeedOpen(false);
    }
  };

  const closeChat = () => {
    setIsChatOpen(false);
  };

  const toggleFooter = () => {
    setIsFooterVisible(!isFooterVisible);
  };

  const handleBackgroundChange = (e) => {
    setBackground(e.target.value);
  };

  const handleFooterToggleVisibilityChange = (e) => {
    setShowFooterToggle(e.target.checked);
  };

  const handleNewsToggleVisibilityChange = (e) => {
    setShowNewsToggle(e.target.checked);
  };

  const handleChatToggleVisibilityChange = (e) => {
    setShowChatToggle(e.target.checked);
  };

  const handleAutoChangeBgChange = (e) => {
    setAutoChangeBg(e.target.checked);
  };



  const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      return <h1></h1>;
    } else {
      return (
        <div className={`countdown ${countdownTheme}-countdown`}>
          <div className="time">
            <span>{days}</span>
            <p>Days</p>
            <div className="time-ring"></div>
          </div>
          <div className="time">
            <span>{hours}</span>
            <p>Hours</p>
            <div className="time-ring"></div>
          </div>
          <div className="time">
            <span>{minutes}</span>
            <p>Minutes</p>
            <div className="time-ring"></div>
          </div>
          <div className="time">
            <span>{seconds}</span>
            <p>Seconds</p>
            <div className="time-ring"></div>
          </div>
        </div>
      );
    }
  };

  const renderBackgroundElement = (bgName, className) => {
    if (!bgName) return null;
    if (bgName.endsWith('.mp4')) {
      return (
        <video key={bgName} ref={videoRef} autoPlay muted loop playsInline className={className}>
          <source src={`${process.env.PUBLIC_URL}/img/backgrounds/videos/${bgName}`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    } else {
      return <img key={bgName} src={`${process.env.PUBLIC_URL}/img/backgrounds/${bgName}`} alt="GTA VI Background" className={className} />;
    }
  };

  const getBackgrounds = () => {
    return (
      <>
        {prevBg && renderBackgroundElement(prevBg, 'bg-fade-out')}
        {renderBackgroundElement(currentBg, isTransitioning ? 'bg-fade-in' : '')}
      </>
    );
  };


  return (
    <div className="container">
      <div className="bg-container">
        {getBackgrounds()}
      </div>
      <div className="overlay"></div>

      <main className="main-content">
        <h1 className="sr-only">GTA VI Countdown Timer | Days Until Grand Theft Auto 6 Release Date</h1>

        {showNewsToggle && (

          <button
            onClick={toggleNewsFeed}
            className={`news-feed-toggle ${showControls || isNewsFeedOpen ? 'visible' : ''} ${isNewsFeedOpen ? 'news-open' : ''}`}
            aria-label="Toggle News Feed"
          >
            <FontAwesomeIcon icon={faNewspaper} />
          </button>
        )}

        <NewsFeed isOpen={isNewsFeedOpen} onClose={closeNewsFeed} />

        {isNearRelease && (
          <>
            <ChatBox isOpen={isChatOpen} onClose={closeChat} />

            <div className={`top-left-controls ${showControls || isNewsFeedOpen || isChatOpen ? 'visible' : ''}`}>
              <ViewerCounter />
            </div>

            {showChatToggle && (
              <button
                onClick={toggleChat}
                className={`chat-toggle ${showControls || isChatOpen ? 'visible' : ''} ${isChatOpen ? 'chat-open' : ''}`}
                aria-label="Toggle Live Chat"
              >
                <FontAwesomeIcon icon={faComments} />
              </button>
            )}
          </>
        )}

        <div className="content">
          <img height="100px" src="https://gtasixcountdown.com/img/icons/Grand_Theft_Auto_VI_logo_(with_gradient).svg.png" alt="Grand Theft Auto VI Official Logo"></img>

          <Countdown
            date={releaseDate}
            renderer={renderer}
          />

          <p className="release-date">NOVEMBER 2026</p>

        </div>
      </main>

      <button
        onClick={toggleCustomizationPanel}
        className={`customize-toggle ${showControls || showCustomizationPanel || isNewsFeedOpen || (isNearRelease && isChatOpen) ? 'visible' : ''}`}
      >
        <FontAwesomeIcon icon={faGear} /> Customize
      </button>

      <div className={`customization-panel ${showCustomizationPanel ? 'show' : ''}`}>
        <div className="panel-header">
          <h2>Customize</h2>
        </div>

        <div className="option">
          <label htmlFor="background">Background:</label>
          <select id="background" ref={backgroundSelectRef} value={background} onChange={handleBackgroundChange}>
            <option value="gta-vi-artwork-bg.webp">GTA VI Artwork</option>

            <option value="gta-vi-trailer2-art.webp">GTA VI Trailer 2 Art</option>
            <option value="Official_Cover_Art_landscape.webp">GTA VI Cover Art</option>
            <option value="ULTIMATE_EDITION_01.16qc1xq5nigg1.webp">Ultimate Edition</option>
            <option value="ULTIMATE_EDITION_02.0q-6.nrtf~jj0.webp">Ultimate Edition 02</option>
            <option value="ULTIMATE_EDITION_SAFEHOUSE_VEHICLES_03.0c9.qtpdi.tf.webp">Ultimate Edition Safe House</option>
            <option value="ULTIMATE_EDITION_GOODTIME_GEAR_01.0t7de8dow381q.webp">Ultimate Edition Good Time Gear</option>
            <option value="Boobie_Ike_01.webp">Boobie_Ike_01</option>
            <option value="Cal_Hampton_03.webp">Cal_Hampton_03</option>
            <option value="Leonida_Keys_01.webp">Leonida_Keys_01</option>
            <option value="Real_Dimez_02.webp">Real_Dimez_02</option>
            <option value="gta-vi-vicecity-sign.webp">Vice City Sign</option>
            <option value="vice-city-beach.webp">Vice City Beach</option>
            <option value="gta-vi-bg-trailer.mp4">GTA VI Trailer Video</option>
            <option value="gta-vi-bg-trailer-2.mp4">GTA VI Trailer 2 Video</option>
          </select>
        </div>

        <div className="checkbox-option">
          <label>
            <input
              type="checkbox"
              id="showFooterToggle"
              checked={showFooterToggle}
              onChange={handleFooterToggleVisibilityChange}
              className="checkbox-input"
            />
            <span className="custom-checkbox"></span>
            Show Footer Toggle
          </label>
        </div>

        <div className="checkbox-option">
          <label>
            <input
              type="checkbox"
              id="showNewsToggle"
              checked={showNewsToggle}
              onChange={handleNewsToggleVisibilityChange}
              className="checkbox-input"
            />
            <span className="custom-checkbox"></span>
            Show News Toggle
          </label>
        </div>

        {isNearRelease && (
          <div className="checkbox-option">
            <label>
              <input
                type="checkbox"
                id="showChatToggle"
                checked={showChatToggle}
                onChange={handleChatToggleVisibilityChange}
                className="checkbox-input"
              />
              <span className="custom-checkbox"></span>
              Show Chat Toggle
            </label>
          </div>
        )}

        <div className="checkbox-option">
          <label>
            <input
              type="checkbox"
              id="autoChangeBg"
              checked={autoChangeBg}
              onChange={handleAutoChangeBgChange}
              className="checkbox-input"
            />
            <span className="custom-checkbox"></span>
            Auto Change Background (20s)
          </label>
        </div>

        <div className="widget-accordion-section">
          <button
            type="button"
            className="widget-accordion-header"
            onClick={() => setShowWidgetSection(!showWidgetSection)}
          >
            <span>Embed Widget</span>
            <FontAwesomeIcon icon={showWidgetSection ? faChevronUp : faChevronDown} />
          </button>

          {showWidgetSection && (
            <div className="widget-accordion-body">
              <div className="widget-control">
                <label htmlFor="widgetBg">Background:</label>
                <select id="widgetBg" value={widgetBg} onChange={(e) => setWidgetBg(e.target.value)}>
                  <option value="default">Default Cover</option>
                  <option value="transparent">Transparent</option>
                  <option value="solid">Solid Dark</option>
                  <option value="gta-vi-artwork-bg.webp">GTA VI Artwork</option>
                  <option value="gta-vi-trailer2-art.webp">GTA VI Trailer 2 Art</option>
                  <option value="vice-city-beach.webp">Vice City Beach</option>
                </select>
              </div>

              <div className="widget-control">
                <label htmlFor="widgetSize">Size Preset:</label>
                <select id="widgetSize" value={widgetSizePreset} onChange={(e) => setWidgetSizePreset(e.target.value)}>
                  <option value="small">Small (320x200)</option>
                  <option value="medium">Medium (380x320)</option>
                  <option value="large">Large (450x400)</option>
                  <option value="custom">Custom Dimensions</option>
                </select>
              </div>

              {widgetSizePreset === 'custom' && (
                <div className="widget-dimensions">
                  <div className="dimension-input">
                    <label htmlFor="widgetW">Width:</label>
                    <input
                      id="widgetW"
                      type="number"
                      value={widgetWidth}
                      onChange={(e) => setWidgetWidth(parseInt(e.target.value) || 0)}
                      min="200"
                      max="1200"
                    />
                  </div>
                  <div className="dimension-input">
                    <label htmlFor="widgetH">Height:</label>
                    <input
                      id="widgetH"
                      type="number"
                      value={widgetHeight}
                      onChange={(e) => setWidgetHeight(parseInt(e.target.value) || 0)}
                      min="150"
                      max="1200"
                    />
                  </div>
                </div>
              )}

              <div className="widget-preview-wrapper">
                <span className="preview-label">Live Preview:</span>
                <div className="widget-preview-frame">
                  <iframe
                    src={`${window.location.origin}${window.location.pathname}#/widget?bg=${widgetBg}`}
                    width={widgetWidth}
                    height={widgetHeight}
                    frameBorder="0"
                    style={{ border: 'none', overflow: 'hidden', borderRadius: '12px' }}
                    scrolling="no"
                    title="GTA 6 Countdown Widget Preview"
                  />
                </div>
              </div>

              <p className="widget-info-text">Copy the code below to embed the GTA 6 countdown widget on your site:</p>
              <textarea
                readOnly
                className="widget-code-box"
                value={`<iframe src="${window.location.origin}${window.location.pathname}#/widget?bg=${widgetBg}" width="${widgetWidth}" height="${widgetHeight}" frameborder="0" style="border:none; overflow:hidden;" scrolling="no"></iframe>`}
                onClick={(e) => e.target.select()}
              />
              <button
                type="button"
                className="copy-widget-btn"
                onClick={() => {
                  navigator.clipboard.writeText(`<iframe src="${window.location.origin}${window.location.pathname}#/widget?bg=${widgetBg}" width="${widgetWidth}" height="${widgetHeight}" frameborder="0" style="border:none; overflow:hidden;" scrolling="no"></iframe>`);
                  alert("Copied widget code to clipboard!");
                }}
              >
                Copy Code
              </button>
            </div>
          )}
        </div>

      </div>

      {showFooterToggle && (
        <button
          className={`footer-toggle ${isFooterVisible ? '' : 'collapsed'} ${showControls || isNewsFeedOpen || (isNearRelease && isChatOpen) ? 'visible' : ''}`}
          onClick={toggleFooter}
        >
          <FontAwesomeIcon icon={isFooterVisible ? faChevronUp : faChevronDown} />
        </button>
      )}

      <footer className={`footer ${isFooterVisible ? '' : 'hidden'}`}>
        <div className="disclaimer">
          This is an unofficial fan-made countdown clock. GTA and all related marks are trademarks of Take-Two Interactive and Rockstar Games. This site is not affiliated with or endorsed by Take-Two Interactive or Rockstar Games.
        </div>
        <div className="credit">
          Made by Rio
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<CountdownApp />} />
        <Route path="/widget" element={<Widget />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Router>
  );
};

export default App;
